import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"

import { Container, Row, Col } from "./Grid"
import Markdown from "./Markdown"
import Button from "./Button"
import Link from "./Link"
import ImageContentful from "./ImageContentful"
import Tag, { TagList as StyledTagList } from "./Tag"

import { media, mixins } from "../styles"

const HeroContainer = styled.div`
  align-items: center;
  font-weight: 300;
  min-height: 360px;
  justify-content: center;
  position: relative;
  z-index: 1;

  ${(props) =>
    props.$overrideHeight &&
    css`
      @media ${media.mdDown} {
        min-height: ${props.$overrideHeight}px;

        ${Header},
        ${Background} {
          height: ${props.$overrideHeight}px;
        }
      }
    `}

  @media ${media.md} {
    margin-bottom: 100px;
  }

  @media ${media.lg} {
    min-height: 470px;
    background: ${({ theme }) => theme.greyLight};
    margin-bottom: 110px;
    padding-bottom: 60px;
  }

  @media ${media.xl} {
    min-height: 640px;
  }
`

export const Header = styled.div`
  height: 360px;
  padding: 50px 0 0;
  margin: 0;
  z-index: 1;
  text-align: center;
  position: relative;

  @media ${media.md} {
    height: auto;
    margin: 0 0 20px;
  }

  @media ${media.lg} {
    padding-top: 60px;
    margin: 0 0 40px;
  }

  @media ${media.xl} {
    margin: 0 0 55px;
    padding-top: 90px;
  }

  h1 {
    ${mixins.title2}
    margin: 0;
  }
`

const SubHeader = styled.h2`
  ${mixins.title5}
  align-items: center;
  display: flex;
  justify-content: center;
  line-height: 1;
  margin: 0 0 20px;

  @media ${media.md} {
    margin: 0 0 10px;
  }

  ${(props) =>
    props.$iconHighlight &&
    css`
      & {
        margin-bottom: 20px;
      }

      ${Icon} {
        background: ${({ theme }) => theme.white};
        border-radius: 10px;
        color: ${({ theme }) => theme.sun};
        height: 48px;
        width: 48px;
      }
    `}
`

const Icon = styled.span`
  flex: none;
  height: 24px;
  margin: 0 10px 0 0;
  width: 24px;

  img {
    display: block;
    max-height: 100%;
    max-width: 100%;
  }

  .wrap--svg {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }
`

const Logo = styled.span`
  display: block;
  flex: none;
  height: auto;
  margin: -20px auto 5px;
  width: 150px;

  img,
  svg {
    max-height: 100%;
    max-width: 100%;
    display: block;
    height: auto;
    width: 100%;
  }

  @media ${media.md} {
    width: 190px;
  }

  @media ${media.lg} {
    width: 245px;
  }
`

const TagList = styled(StyledTagList)`
  display: none;
  margin: -25px 0 40px;
  justify-content: center;

  @media ${media.lg} {
    display: flex;
    margin: -20px 0 30px;
  }

  @media ${media.xl} {
    margin: -25px 0 40px;
  }
`

export const Text = styled.div`
  ${mixins.copy3}
  text-align: center;
  position: relative;
  margin: 0 0 30px;
  z-index: 2;

  p:last-child {
    margin: 0;
  }

  @media ${media.lg} {
    text-align: left;
    margin: 0 0 25px;
  }

  @media ${media.xl} {
    margin: 0 0 30px;
  }
`

const Illustration = styled.div`
  margin: 0 10px 55px;
  width: calc(100% - 20px);
  pointer-events: none;
  user-select: none;

  @media ${media.lg} {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    margin: 0;
  }

  img {
    display: block;
    width: 100%;
  }
`

const Background = styled.div`
  height: 360px;
  background: ${({ theme }) => theme.greyLight};
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  z-index: -1;

  @media ${media.lg} {
    display: none;
    height: 470px;
  }

  @media ${media.xl} {
    height: 640px;
  }
`

const HeroButtonRow = styled.div`
  align-items: center;
  display: flex;
  flex-flow: column;

  > * + * {
    margin: 20px 0 0;
  }

  @media ${media.md} {
    flex-flow: row nowrap;
    justify-content: center;

    > * + * {
      margin: 0 0 0 20px;
    }
  }

  @media ${media.lg} {
    justify-content: flex-start;

    > * + * {
      margin: 0 0 0 10px;
    }
  }

  @media ${media.xl} {
    > * + * {
      margin: 0 0 0 20px;
    }
  }
`

const Hero = ({
  background,
  contentExtra,
  contentLeft,
  header,
  heroExtraButton,
  icon,
  iconHighlight,
  illustration,
  linkText,
  linkUrl,
  logo,
  overrideHeight,
  subHeader,
  text,
  tags,
}) => {
  return (
    <Container>
      <HeroContainer $overrideHeight={overrideHeight} data-testid="hero">
        <Row>
          {header && (
            <Col
              col="10"
              xsOffset="1"
              sm="10"
              smOffset="1"
              md="8"
              mdOffset="2"
              lg="6"
              lgOffset="3"
              xl="8"
              xlOffset="2"
            >
              <Header>
                {subHeader && (
                  <SubHeader $iconHighlight={iconHighlight}>
                    {icon && (
                      <Icon aria-hidden="true">
                        <ImageContentful image={icon} alt="" inline />
                      </Icon>
                    )}
                    <span>{subHeader}</span>
                  </SubHeader>
                )}
                {logo && (
                  <Logo aria-hidden="true">
                    <ImageContentful image={logo} alt="" inline />
                  </Logo>
                )}
                <h1>{header}</h1>
              </Header>
              {tags?.length > 0 ? (
                <TagList>
                  {tags.map((tag, i) => (
                    <Tag key={i} secondary={tag.secondary}>
                      {tag.name}
                    </Tag>
                  ))}
                </TagList>
              ) : null}
            </Col>
          )}

          {illustration && (
            <Illustration aria-hidden="true">{illustration}</Illustration>
          )}

          <Col md="10" mdOffset="1" lg="6" lgOffset="0">
            {contentLeft}
          </Col>

          <Col md="10" mdOffset="1" lg="5" lgOffset="0">
            {text && (
              <Text>
                <Markdown>{text}</Markdown>
              </Text>
            )}
            {contentExtra}
            {(linkText && linkUrl) || heroExtraButton ? (
              <HeroButtonRow>
                {linkText && linkUrl ? (
                  <Button as={Link} to={linkUrl} arrow intl={false}>
                    {linkText}
                  </Button>
                ) : null}
                {heroExtraButton ? heroExtraButton : null}
              </HeroButtonRow>
            ) : null}
          </Col>
        </Row>
        <Background aria-hidden={true} $overrideHeight={overrideHeight}>
          {background}
        </Background>
      </HeroContainer>
    </Container>
  )
}

Hero.propTypes = {
  background: PropTypes.node,
  contentExtra: PropTypes.node,
  contentLeft: PropTypes.node,
  header: PropTypes.string,
  heroExtraButton: PropTypes.node,
  icon: PropTypes.object,
  iconHighlight: PropTypes.bool,
  illustration: PropTypes.node,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  logo: PropTypes.object,
  overrideHeight: PropTypes.number,
  subHeader: PropTypes.string,
  text: PropTypes.string,
}

export default Hero
